import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Download, FileText, Mail, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { formatDateShort } from '@/lib/utils';
import { useUIStore } from '@/stores/ui-store';

interface Entitlement {
  id: string;
  download_token: string;
  download_count: number;
  max_downloads: number | null;
  expires_at: string | null;
  created_at: string;
  file_name: string;
  file_size_bytes: number;
  file_format: string | null;
  mime_type: string;
  product_name: string;
  product_id: string;
  image_url: string | null;
  order_id: string;
  order_number: string;
  purchased_at: string;
}

type StatusInfo = { text: string; tone: 'ok' | 'warn' | 'gone' };

export default function MyDownloads() {
  const { t } = useTranslation('account');
  const [entitlements, setEntitlements] = useState<Entitlement[] | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [resendingOrderId, setResendingOrderId] = useState<string | null>(null);
  const addToast = useUIStore((s) => s.addToast);

  useEffect(() => {
    api
      .get<{ entitlements: Entitlement[] }>('/downloads')
      .then((r) => setEntitlements(r.entitlements || []))
      .catch(() => setEntitlements([]));
  }, []);

  const fmtSize = (bytes: number): string => {
    if (bytes < 1024) return t('downloads.size.bytes', { value: bytes });
    if (bytes < 1024 * 1024)
      return t('downloads.size.kilobytes', { value: (bytes / 1024).toFixed(1) });
    return t('downloads.size.megabytes', { value: (bytes / 1024 / 1024).toFixed(1) });
  };

  const statusLabel = (e: Entitlement): StatusInfo => {
    if (e.expires_at && new Date(e.expires_at) < new Date())
      return { text: t('downloads.status.expired'), tone: 'gone' };
    if (e.max_downloads != null && e.download_count >= e.max_downloads)
      return { text: t('downloads.status.limitReached'), tone: 'gone' };

    const parts: string[] = [];
    if (e.max_downloads != null) {
      const left = e.max_downloads - e.download_count;
      parts.push(t('downloads.status.downloadsLeft', { count: left }));
    }
    if (e.expires_at) {
      parts.push(
        t('downloads.status.availableUntil', { date: formatDateShort(e.expires_at) }),
      );
    }
    return parts.length > 0
      ? { text: parts.join(' · '), tone: 'warn' }
      : { text: t('downloads.status.unlimited'), tone: 'ok' };
  };

  const handleDownload = async (e: Entitlement) => {
    setDownloadingId(e.id);
    try {
      const res = await api.get<{ download_url: string; file_name: string }>(
        `/downloads/${e.id}`,
      );
      window.location.href = res.download_url;
      // Optimistic increment so the UI stays in sync
      setEntitlements((prev) =>
        (prev || []).map((x) =>
          x.id === e.id ? { ...x, download_count: x.download_count + 1 } : x,
        ),
      );
    } catch (err: any) {
      addToast(err?.message || t('downloads.downloadFailed'), 'error');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleResend = async (orderId: string) => {
    setResendingOrderId(orderId);
    try {
      await api.post('/downloads/resend-email', { order_id: orderId });
      addToast(t('downloads.resendSuccess'), 'success');
    } catch (err: any) {
      addToast(err?.message || t('downloads.resendFailed'), 'error');
    } finally {
      setResendingOrderId(null);
    }
  };

  if (entitlements === null) return <LoadingSpinner className="py-16" />;

  if (entitlements.length === 0) {
    return (
      <div className="text-center py-16">
        <FileText className="mx-auto h-12 w-12 text-ink-muted mb-4" aria-hidden="true" />
        <h2 className="text-xl font-semibold text-ink mb-2">
          {t('downloads.emptyHeading')}
        </h2>
        <p className="text-ink-muted mb-6">{t('downloads.emptyDescription')}</p>
        <Link to="/shop">
          <Button>{t('downloads.browseShop')}</Button>
        </Link>
      </div>
    );
  }

  // Group by order so the resend-email button is meaningful
  const byOrder = new Map<string, { orderNumber: string; purchasedAt: string; items: Entitlement[] }>();
  for (const e of entitlements) {
    if (!byOrder.has(e.order_id)) {
      byOrder.set(e.order_id, {
        orderNumber: e.order_number,
        purchasedAt: e.purchased_at,
        items: [],
      });
    }
    byOrder.get(e.order_id)!.items.push(e);
  }

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-2xl font-bold text-ink">{t('downloads.heading')}</h1>
        <p className="text-ink-muted mt-1">{t('downloads.subheading')}</p>
      </header>

      {Array.from(byOrder.entries()).map(([orderId, order]) => (
        <section key={orderId} className="bg-surface border border-line rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm font-semibold text-ink">
                {t('downloads.orderHeading', { number: order.orderNumber })}
              </p>
              <p className="text-xs text-ink-muted">{formatDateShort(order.purchasedAt)}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleResend(orderId)}
              disabled={resendingOrderId === orderId}
            >
              {resendingOrderId === orderId ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Mail className="w-4 h-4" />
              )}
              {t('downloads.resendEmail')}
            </Button>
          </div>

          <ul className="divide-y divide-line">
            {order.items.map((e) => {
              const status = statusLabel(e);
              const disabled = status.tone === 'gone' || downloadingId === e.id;
              return (
                <li key={e.id} className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="w-5 h-5 text-ink-muted flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-ink truncate">{e.product_name}</p>
                      <p className="text-xs text-ink-muted truncate">
                        {e.file_name} · {(e.file_format || '').toUpperCase()} · {fmtSize(e.file_size_bytes)}
                      </p>
                      <p
                        className={
                          'text-xs mt-0.5 ' +
                          (status.tone === 'gone'
                            ? 'text-red-600'
                            : status.tone === 'warn'
                              ? 'text-amber-600'
                              : 'text-ink-muted')
                        }
                      >
                        {status.text}
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleDownload(e)}
                    disabled={disabled}
                  >
                    {downloadingId === e.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    {t('downloads.download')}
                  </Button>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}
